import { Box, Grid, Typography } from '@mui/material'
import ClinicApi from 'api/master/clinic.api'
import ReportApi, { FindAllReportInterface } from 'api/report.api'
import HeaderCard from 'component/CardCustom/HeaderCard'
import { useDownloadExcel } from 'component/Excel/hooks/useExcel'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import _ from 'lodash'
import moment from 'moment'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import SelectCheckBoxInput from 'new-components/inputs/select_checkbox_input'
import Loading from 'new-components/loading/loading'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { routeName } from 'routes/routes-name'
import { getBranch, numberFormat, toBuddhistYear } from 'utils/app.utils'
import { dateToView } from 'utils/date.utils'
import IncomeExpenseList from './IncomeExpenseList'

export default function IncomeExpense() {
    const { t } = useTranslation()
    const history = useHistory()
    const componentRef = useRef<HTMLDivElement>(null)

    const [branchId, setBranchId] = useState<any>(getBranch())
    const [branches, setBranches] = useState<any[]>([])
    const [branchSelect, setBranchSelect] = useState<number>(getBranch())
    const momentDefaultRange = (): string[] => [moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
    const [rangeDate, setRangeDate] = useState<string[]>(momentDefaultRange)

    const [rowCount, setRowCount] = useState<number>(0)
    const [sortBy, setSortBy] = useState<string>('createAt')
    const [sortType, setSortType] = useState<string>('ASC')
    const [fetch, setFetch] = useState(false)
    const [rows, setRows] = useState<any[]>([])
    const [rowsExport, setRowsExport] = useState<any[]>([])
    const [page, setPage] = useState<number>(1)
    const [pageLimit, setPageLimit] = useState<number>(10)
    const [loading, setLoading] = useState<boolean>(true)

    const typeOption = [{ name: t("INCOME_EXPENSES.TABLE.CELL.EXPENSE_GROUP.INCOME.TITLE"), id: "INCOME" }, { name: t("INCOME_EXPENSES.TABLE.CELL.EXPENSE_GROUP.EXPENSE.TITLE"), id: "EXPENSE" }]
    const [typeSelect, setTypeSelect] = useState<string[]>(typeOption.map((item) => item.id))
    const statusOption = [{ name: t("INCOME_EXPENSES.DOCUMENT_STATUS.PENDING_APPROVAL"), id: "PENDING_APPROVAL" }, { name: t("INCOME_EXPENSES.DOCUMENT_STATUS.APPROVAL"), id: "APPROVAL" }, { name: t("INCOME_EXPENSES.DOCUMENT_STATUS.UNAPPROVAL"), id: "UNAPPROVAL" }]
    const [statusSelect, setStatusSelect] = useState<string[]>(statusOption.map((item) => item.id))

    const [staticCondition, setStaticCondition] = useState({
        page: page,
        // pageLimit: pageLimit,
        sortBy: sortBy,
        sortType: sortType,
        branchId,
        dateStart: rangeDate[0],
        dateEnd: rangeDate[1],
        status: statusSelect?.join(',') || '',
        type: typeSelect?.join(',') || ''
    });
    const [isExport, setIsExport] = useState(true)

    const verifyPermission = async () => {
        const condition: FindAllReportInterface = {
            page: 1,
            pageLimit: 200
        }
        ReportApi.reportList(condition).then(({ data }) => {
            const check = _.find(data, (val: any) => String(val.reportKey) === 'INCOME_EXPENSE') ? true : false
            if (!check) {
                history.push(routeName.report)
                return false
            }
        })

        ClinicApi.findAllBranches()
            .then(({ data }) => setBranches(data.map((item: any) => ({ name: item?.branchName, value: item?.branchId }))))
            .catch((e) => {
                return
            })

        return true
    }

    useEffect(() => {
        verifyPermission()
    }, [branchId])



    const fetchDF = async (isExport?: boolean) => {
        let condition: any = { ...staticCondition, pageLimit: pageLimit };

        if (isExport) condition = { ...condition, export: '1' };

        setLoading(true);
        ReportApi.reportIncomeExpense(condition)
            .then(({ headers, data }) => {
                if (!isExport) {
                    setRows(data.rows);
                    setRowCount(headers['x-total']);
                } else {
                    setIsExport(false)
                    setRowsExport(data.rows);
                }
            })
            .catch((e) => console.error(e))
            .finally(() => {
                setLoading(false);
            });
    };

    const { onDownload } = useDownloadExcel({
        tableExportRef: componentRef.current,
        filename: `${t(`REPORT.REPORT_NAME.INCOME_EXPENSE`)}`,
        sheetName: `${t(`REPORT.REPORT_NAME.INCOME_EXPENSE`)}`
    })

    const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
        setSortType(sortTypeVal)
        setSortBy(sortByVal)
    }

    const handleChangeRowsPerPage = (limit: number) => setPageLimit(limit)

    const handleChangePage = (val: number) => handlePageChange(val)

    useEffect(() => {
        isExport && fetchDF(true)
    }, [isExport, staticCondition])

    useEffect(() => {
        fetchDF()
    }, [staticCondition])

    const handlePageChange = (newPage: number) => {
        setStaticCondition({
            ...staticCondition,
            page: newPage || staticCondition.page
        });
    };

    const handleButtonClick = () => {
        setStaticCondition({
            ...staticCondition,
            page: 1,
            branchId,
            dateStart: rangeDate[0],
            dateEnd: rangeDate[1],
            status: statusSelect?.join(',') || '',
            type: typeSelect?.join(',') || ''
        });
        setIsExport(true)
    };

    const { dataIncome, dataExpense } = useMemo(() => {
        const inc: any[] = []
        const exp: any[] = []
        if (rowsExport?.length) {
            rowsExport.forEach((item: any) => {
                if (item?.expenseGroup === 'EXPENSE') exp.push(item)
                else inc.push(item)
            })
        }
        return { dataIncome: inc, dataExpense: exp }
    }, [rowsExport])

    const branchName = _.get(_.find(branches, { value: branchId }), 'name') || ''
    const validDate = (d: string) => {
        const formatted = toBuddhistYear(moment(d))
        return formatted !== 'Invalid dateNaN' ? formatted : '-'
    }

    const headRow = [
        t('REPORT.TABLE.FOLLOW_UP.#'),
        t('REPORT.TABLE.STOCK.DOCUMENT_NO'),
        t('INCOME_EXPENSES.TABLE.CELL.HEADER.DOCUMENT_CREATION_DATE'),
        t('REPORT.TABLE.SALE_SUMMARY_MONTH.TYPE'),
        t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.LIST'),
        t('REPORT.TABLE.COMMISSION.QTY'),
        t('REPORT.TABLE.INCOME_EXPENSE.PRICEBAHT'),
        t('REPORT.TABLE.INCOME_EXPENSE.TOTALBAHT'),
        t('STATUS.LABEL'),
        t('REPORT.TABLE.INCREASE.UPDATED_BY'),
    ]
    const subHeadRow = [
        t('REPORT.TABLE.FOLLOW_UP.#'),
        t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.LIST'),
        t('REPORT.TABLE.COMMISSION.QTY'),
        t('REPORT.TABLE.INCOME_EXPENSE.PRICEBAHT'),
        t('REPORT.TABLE.INCOME_EXPENSE.TOTALBAHT'),
    ]

    const cellTd = { fontSize: '9.5px', fontWeight: 500, padding: '4px', verticalAlign: 'top' as const, border: '1px solid black' }
    const headTd = { fontSize: '9.5px', fontWeight: 700, padding: '4px', border: '1px solid black' }

    return (
        <>
            <Box>
                <HeaderCard text={t(`REPORT.REPORT_NAME.INCOME_EXPENSE`)} />
                <Box sx={{ px: 3, pt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3} xl={2}>
                            <SelectInput
                                label={t('NAV.BRANCH')}
                                onChange={(e) => setBranchSelect(e.target.value)}
                                options={branches}
                                value={branchSelect.toString()}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} xl={2}>
                            <InputSecondNewRangePicker
                                inputHeight={40}
                                value={rangeDate || emptyRangePicker}
                                onchange={(val: any) => {
                                    if (!_.isEmpty(val)) setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                                }}
                                label={''}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} xl={2}>
                            <SelectCheckBoxInput
                                label={t('REPORT.TABLE.SALE_SUMMARY_MONTH.TYPE')}
                                onChange={(e) => setTypeSelect(e)}
                                options={typeOption}
                                selected={typeSelect}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} xl={2}>
                            <SelectCheckBoxInput
                                label={t('STATUS.LABEL')}
                                onChange={(e) => setStatusSelect(e)}
                                options={statusOption}
                                selected={statusSelect}
                            />

                        </Grid>
                        <Grid item xs={12} sm={6} md={12} xl={4}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                                <MainButton
                                    onClick={() => {
                                        handleButtonClick()
                                    }}
                                    title={t('INPUT.SEARCH')}
                                    type={'primary'}
                                    variant={'contained'}
                                />
                                <DropdownButton
                                    title={t('REGISTER.BUTTON.PRINT_DOCUMENT')}
                                    onChange={(e) => {
                                        if (e === 'Excel') onDownload()
                                        else if (e === 'PDF') {
                                            const style = document.createElement('style')
                                            style.innerHTML = `
                                                @media print {
                                                    @page { size: A4 portrait }
                                                }
                                            `
                                            document.head.appendChild(style)
                                            window.print()
                                            document.head.removeChild(style)
                                        }
                                    }}
                                    options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Box marginTop="16px">
                        <IncomeExpenseList
                            page={staticCondition.page}
                            pageLimit={pageLimit}
                            rowCount={rowCount}
                            sortBy={staticCondition.sortBy}
                            sortType={staticCondition.sortType}
                            data={rows}
                            dataIncome={dataIncome}
                            dataExpense={dataExpense}
                            onRequestSort={onRequestSort}
                            handleChangeRowsPerPage={handleChangeRowsPerPage}
                            handleChangePage={handleChangePage}
                            setSortType={setSortType}
                            setSortBy={setSortBy}
                        />
                    </Box>
                    {loading && <Loading />}
                </Box>
            </Box>

            <Box id="print-table-container" ref={componentRef}>
                <Typography fontSize="14px" fontWeight={500}>{t(`REPORT.REPORT_NAME.INCOME_EXPENSE`)}</Typography>
                <Box display="flex" flexDirection="row" alignItems="center" gap="12px" flexWrap="wrap" mb={1}>
                    <Typography fontSize="10px" fontWeight={500}>{t('REPORT.TABLE.DECREASE.BRANCH')}: {branchName || '-'}</Typography>
                    <Typography fontSize="10px" fontWeight={500}>{t('REPORT.TABLE.DECREASE.DATE_START')}: {validDate(rangeDate[0])}</Typography>
                    <Typography fontSize="10px" fontWeight={500}>{t('REPORT.TABLE.DECREASE.DATE_END')}: {validDate(rangeDate[1])}</Typography>
                    <Typography fontSize="10px" fontWeight={500}>{t('REPORT.TABLE.SALE_SUMMARY_MONTH.TYPE')}: {typeSelect?.length === 2 ? t('REPORT.FILTER.ALL') : typeSelect?.length ? typeSelect.map((item) => t(`INCOME_EXPENSES.TABLE.CELL.EXPENSE_GROUP.${item}.TITLE`)).join(', ') : '-'}</Typography>
                    <Typography fontSize="10px" fontWeight={500}>{t('REPORT.TABLE.DECREASE.STATUS')}: {statusSelect?.length === 3 ? t('REPORT.FILTER.ALL') : statusSelect?.length ? statusSelect.map((item) => t(`INCOME_EXPENSES.DOCUMENT_STATUS.${item}`)).join(', ') : '-'}</Typography>
                </Box>

                <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ textAlign: 'center' }}>
                            {headRow.map((label, i) => <th key={i} style={headTd}>{label}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {rowsExport?.length ? rowsExport.map((data: any, idx: number) => (
                            <tr key={idx}>
                                <td style={{ ...cellTd, textAlign: 'center' }}>{idx + 1}</td>
                                <td style={{ ...cellTd, textAlign: 'left' }}>{data?.documentNo || '-'}</td>
                                <td style={{ ...cellTd, textAlign: 'center' }}>{dateToView(data?.documentDate)}</td>
                                <td style={{ ...cellTd, textAlign: 'left' }}>{t(`INCOME_EXPENSES.TABLE.CELL.EXPENSE_GROUP.${data?.expenseGroup}.TITLE`)}</td>
                                <td style={{ ...cellTd, textAlign: 'left' }}>{data?.expenseTypeName || '-'}</td>
                                <td style={{ ...cellTd, textAlign: 'right' }}>{data?.qty ?? '-'}</td>
                                <td style={{ ...cellTd, textAlign: 'right' }}>{numberFormat(data?.price || 0)}</td>
                                <td style={{ ...cellTd, textAlign: 'right' }}>{numberFormat(data?.total || 0)}</td>
                                <td style={{ ...cellTd, textAlign: 'center' }}>{t(`INCOME_EXPENSES.DOCUMENT_STATUS.${data?.status}`)}</td>
                                <td style={{ ...cellTd, textAlign: 'center' }}>{data?.updatedBy || '-'}</td>
                            </tr>
                        )) : (
                            <tr><td colSpan={headRow.length} style={{ ...cellTd, textAlign: 'center' }}>{t('NOT_FOUND')}</td></tr>
                        )}
                    </tbody>
                </table>

                {[
                    { titleKey: 'REPORT.TABLE.INCOME_EXPENSE.SUM_INCOME', data: dataIncome },
                    { titleKey: 'REPORT.TABLE.INCOME_EXPENSE.SUM_EXPENSES', data: dataExpense },
                ].map((section, idx) => (
                    <Box key={idx} marginTop="20px" sx={{ width: '500px' }} className="page-break-inside">
                        <Typography fontSize="12px" fontWeight={500} mb={0.5}>{t(section.titleKey)}</Typography>
                        <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ textAlign: 'center' }}>
                                    {subHeadRow.map((label, i) => <th key={i} style={headTd}>{label}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {section.data?.length ? (
                                    <>
                                        {section.data.map((data: any, i: number) => (
                                            <tr key={i}>
                                                <td style={{ ...cellTd, textAlign: 'center' }}>{i + 1}</td>
                                                <td style={{ ...cellTd, textAlign: 'left' }}>{data?.expenseTypeName || '-'}</td>
                                                <td style={{ ...cellTd, textAlign: 'right' }}>{data?.qty ?? '-'}</td>
                                                <td style={{ ...cellTd, textAlign: 'right' }}>{numberFormat(data?.price || 0)}</td>
                                                <td style={{ ...cellTd, textAlign: 'right' }}>{numberFormat(data?.total || 0)}</td>
                                            </tr>
                                        ))}
                                        <tr style={{ backgroundColor: '#E0E0E0', fontWeight: 700 }}>
                                            <td colSpan={4} style={{ ...cellTd, textAlign: 'right', fontWeight: 700 }}>{t('REPORT.TITLE.SUM')}</td>
                                            <td style={{ ...cellTd, textAlign: 'right', fontWeight: 700 }}>{numberFormat(_.sumBy(section.data, 'total') || 0)}</td>
                                        </tr>
                                    </>
                                ) : (
                                    <tr><td colSpan={subHeadRow.length} style={{ ...cellTd, textAlign: 'center' }}>{t('NOT_FOUND')}</td></tr>
                                )}
                            </tbody>
                        </table>
                    </Box>
                ))}
            </Box>
        </>
    )
}
